import { useState, useEffect } from "react";
import { Modal, Form, Button } from "react-bootstrap";
import Propdata_types from "prop-types";
import { useTranslation } from "react-i18next";
import { InputText, InputSelect } from "@2solve/tosolve-inputs";

function AttributesValueModal({
  show,
  onHide,
  attribute,
  categoryItems = [],
  postData,
  error,
  setError,
  onDataAdded,
}) {
  const { t } = useTranslation();
  const [values, setValues] = useState([]);
  const [currentCategoryValue, setCurrentCategoryValue] = useState("");
  const [currentItemIds, setCurrentItemIds] = useState([]);

  useEffect(() => {
    if (show) {
      setValues([]);
      setCurrentCategoryValue("");
      setCurrentItemIds([]);
      setError("");
    }
  }, [show]);

  const canClose = () => {
    if (!attribute?.is_required || categoryItems.length === 0) return true;
    return validateRequiredAttribute();
  };

  const closeModal = () => {
    if (!canClose()) {
      setError(
        t(
          "Complete os valores obrigatórios para todos os itens do recurso antes de fechar.",
        ),
      );
      return;
    }
    setValues([]);
    setCurrentCategoryValue("");
    setCurrentItemIds([]);
    setError("");
    onHide();
  };

  // Filtrar itens já adicionados para não permitir repetição
  const availableItems = categoryItems.filter(
    (item) => !values.some((v) => v.item_id === item.id),
  );

  const itemOptions = availableItems.map((item) => ({
    value: item.id,
    label:
      item.name ||
      item.slug ||
      item.title ||
      item.email ||
      item.mapping_group?.name + " > " + item.variable?.name ||
      "",
  }));

  const handleAddCategoryValues = () => {
    if (!currentCategoryValue.trim()) {
      setError(t("O valor não pode estar vazio"));
      return;
    }

    // Validar conforme o tipo do atributo
    if (attribute.data_type === "N" && isNaN(currentCategoryValue)) {
      setError(t("O valor deve ser numérico"));
      return;
    }

    if (currentItemIds.length === 0) {
      setError(t("Selecione pelo menos um item do recurso"));
      return;
    }

    const newValues = [];
    currentItemIds.forEach((itemId) => {
      const selectedItem = categoryItems.find((item) => item.id === itemId);
      if (selectedItem && !values.some((v) => v.item_id === itemId)) {
        newValues.push({
          id: Date.now() + Math.random(), // ID único
          value: currentCategoryValue,
          data_type: attribute.data_type,
          item_id: itemId,
        });
      }
    });

    setValues([...values, ...newValues]);
    setCurrentCategoryValue("");
    setCurrentItemIds([]);
    setError("");
  };

  const handleRemoveValue = (id) => {
    setValues(values.filter((v) => v.id !== id));
  };

  const validateRequiredAttribute = () => {
    // Se o atributo é obrigatório, deve ter um valor para cada item do recurso
    if (attribute?.is_required && categoryItems.length > 0) {
      const itemsWithValues = categoryItems.filter((item) =>
        values.some((v) => v.item_id === item.id),
      );
      return itemsWithValues.length === categoryItems.length;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (values.length === 0) {
      setError(t("Adicione pelo menos um valor"));
      return;
    }

    // Validar se atributo obrigatório tem valores para todos os itens
    if (attribute?.is_required && categoryItems.length > 0) {
      if (!validateRequiredAttribute()) {
        const itemsWithValues = categoryItems.filter((item) =>
          values.some((v) => v.item_id === item.id),
        );
        const missingCount = categoryItems.length - itemsWithValues.length;
        setError(
          t(
            `Atributo obrigatório: faltam atribuir valores para ${missingCount} item(ns) do recurso`,
          ),
        );
        return;
      }
    }

    try {
      const payload = values.map((v) => ({
        attribute_id: attribute.id,
        value: v.value,
        record_id: v.item_id,
      }));

      await postData("/attribute-values", payload);
      onDataAdded();
      closeModal();
    } catch (err) {
      console.error(err);
      setError(t("Erro ao cadastrar valores do atributo"));
    }
  };

  const DATA_TYPE_LABEL = {
    S: t("Texto"),
    N: t("Numérico"),
    B: t("Booleano"),
  };
  if (!attribute) return null;

  return (
    <Modal
      backdrop="static"
      size="lg"
      centered
      show={show}
      onHide={canClose() ? closeModal : undefined}
    >
      <Modal.Header closeButton={canClose()} closeVariant="white">
        <Modal.Title>
          {t("add_values_to")} <strong>{attribute.name}</strong>
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <div className="mb-4">
            <label className="form-label">
              {t("attribute_type")}{" "}
              <strong>{DATA_TYPE_LABEL[attribute.data_type]}</strong>
              {attribute?.is_required && (
                <span className="text-danger ms-2">{t("asterisk")}</span>
              )}
            </label>
            {attribute?.is_required && categoryItems.length > 0 && (
              <div className="alert alert-info mt-2 mb-0" role="alert">
                <small>
                  {t(
                    "Este atributo é obrigatório. Você deve atribuir um valor para cada item do recurso.",
                  )}{" "}
                  {
                    categoryItems.filter((item) =>
                      values.some((v) => v.item_id === item.id),
                    ).length
                  }
                  {t("slash")}
                  {categoryItems.length}
                </small>
              </div>
            )}
          </div>

          {/* Seleção de itens do recurso */}
          {categoryItems.length > 0 && availableItems.length > 0 && (
            <div className="mb-4">
              <label className="form-label mb-3">
                {t("assign_value_to_items")}
              </label>
              {/* Entrada para valor do recurso conforme tipo do atributo */}
              {(attribute.data_type === "S" || attribute.data_type === "N") && (
                <InputText
                  id="categoryValue"
                  data_type={attribute.data_type === "N" ? "number" : "text"}
                  placeholder={
                    attribute.data_type === "N"
                      ? t("Digite um valor numérico para os itens selecionados")
                      : t("Digite o valor para os itens selecionados")
                  }
                  value={currentCategoryValue}
                  onChange={(_, value) => {
                    setCurrentCategoryValue(value);
                    setError("");
                  }}
                />
              )}
              {attribute.data_type === "B" && (
                <InputSelect
                  placeholder={t(
                    "Selecione um valor para os itens selecionados",
                  )}
                  id="categoryBooleanValue"
                  data_type="select"
                  options={[
                    { value: "true", label: t("Verdadeiro") },
                    { value: "false", label: t("Falso") },
                  ]}
                  isMultiple={false}
                  selectAll={false}
                  closeOnSelect={true}
                  IsClearable={false}
                  disabled={false}
                  onChange={(_, selectedValue) => {
                    setCurrentCategoryValue(selectedValue);
                    setError("");
                  }}
                  value={currentCategoryValue}
                />
              )}
              <InputSelect
                placeholder={t("Selecione um ou mais itens do recurso")}
                id="categoryItems"
                data_type="select"
                options={itemOptions}
                isMultiple={true}
                selectAll={false}
                closeOnSelect={false}
                IsClearable={true}
                disabled={false}
                onChange={(_, selectedIds) => {
                  setCurrentItemIds(selectedIds || []);
                  setError("");
                }}
                value={currentItemIds}
              />
              <Button
                variant="outline-secondary"
                className="mt-2"
                onClick={handleAddCategoryValues}
              >
                {t("add_value_to_selected")}
              </Button>
            </div>
          )}

          {/* Lista de valores adicionados */}
          <div className="mb-4">
            <label className="form-label">
              {t("added_values")} {values.length}
            </label>
            <div
              className="p-3 border rounded"
              style={{
                minHeight: "100px",
                maxHeight: "300px",
                overflow: "auto",
              }}
            >
              {values.length === 0 ? (
                <p className="text-muted">{t("no_values_added")}</p>
              ) : (
                <ul className="list-group">
                  {values.map((val) => {
                    const itemName = val.item_id
                      ? categoryItems.find((item) => item.id === val.item_id)
                          ?.name || "Item não encontrado"
                      : null;
                    return (
                      <li
                        key={val.id}
                        className="list-group-item d-flex justify-content-between align-items-center gap-2"
                      >
                        <div style={{ minWidth: 0, wordBreak: "break-word", overflowWrap: "break-word" }}>
                          <strong>
                            {val.value}
                            {itemName && ` - ${itemName}`}
                          </strong>
                          <br />
                        </div>
                        <Button
                          variant="danger"
                          size="sm"
                          style={{ flexShrink: 0, whiteSpace: "nowrap" }}
                          onClick={() => handleRemoveValue(val.id)}
                        >
                          {t("button_remove")}
                        </Button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>

          {error && <div className="text-danger mt-3">{error}</div>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal}>
            {t("button_cancel")}
          </Button>
          <Button variant="primary" type="submit">
            {t("save_values")}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

AttributesValueModal.propdata_types = {
  show: Propdata_types.bool,
  onHide: Propdata_types.func,
  attribute: Propdata_types.object,
  categoryItems: Propdata_types.array,
  postData: Propdata_types.func,
  error: Propdata_types.string,
  setError: Propdata_types.func,
  onDataAdded: Propdata_types.func,
};

export default AttributesValueModal;
